import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import AppLayout from "../components/AppLayout";
import SelectField from "../components/ui/SelectField";
import {
  ArrowLeft,
  CheckCircle2,
  FileDown,
  RefreshCcw,
  Save,
  MessageCircle,
  X,
  Pencil,
  Send,
  Loader2,
  Trash2,
  ShieldOff,
  Mail,
} from "lucide-react";
import { toast } from "react-toastify";
import { PawPrint } from "lucide-react";

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const normalized = String(value).replace(",", ".").trim();
  if (!normalized) return 0;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const compareAnalitoCod = (a, b) =>
  String(a?.analito_cod ?? "").localeCompare(
    String(b?.analito_cod ?? ""),
    "es",
    {
      numeric: true,
      sensitivity: "base",
    },
  );

const evaluateFormula = (formula, valuesByPosition) => {
  if (!formula || typeof formula !== "string") return null;

  const normalizedFormula = formula.replace(/\s+/g, "").toUpperCase();
  if (!normalizedFormula) return null;
  if (!/^[A0-9+\-*/().]+$/.test(normalizedFormula)) return null;

  const numericFormula = normalizedFormula.replace(/A(\d+)/g, (_, index) => {
    const value = valuesByPosition[Number(index)];
    return String(toNumber(value));
  });

  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${numericFormula});`)();
    if (!Number.isFinite(result)) return null;
    return Number(result).toFixed(3);
  } catch (error) {
    return null;
  }
};

const computeManualOverrides = (rows = []) => {
  const overrides = new Set();
  rows.forEach((item, index) => {
    if (
      item.comodin_cod &&
      Array.isArray(item.comodin_opciones) &&
      item.comodin_opciones.length > 0 &&
      item.resultado
    ) {
      const validValues = item.comodin_opciones.map(
        (op) => op.detalle || op.codigo,
      );
      if (!validValues.includes(item.resultado)) {
        overrides.add(index);
      }
    }
  });
  return overrides;
};

const applyFormulaCalculations = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;

  // Only caratula_basica items define formula positions (A1, A2, …).
  // resul_lab-only items (is_from_caratula === false) must not shift positions.
  const caratulaItems = rows
    .filter((item) => item.is_from_caratula !== false)
    .sort(compareAnalitoCod);

  const valuesByPosition = {};
  caratulaItems.forEach((item, index) => {
    valuesByPosition[index + 1] = item?.resultado;
  });

  const formulaResults = {};
  caratulaItems.forEach((item, index) => {
    const calculated = evaluateFormula(item?.formula, valuesByPosition);
    if (calculated !== null) {
      formulaResults[String(item.analito_cod)] = calculated;
      valuesByPosition[index + 1] = calculated;
    }
  });

  // Return a new array; create new objects only for formula items.
  return rows.map((item) => {
    const newVal = formulaResults[String(item.analito_cod)];
    return newVal !== undefined ? { ...item, resultado: newVal } : item;
  });
};

export default function ResultadoDetalle() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [detalle, setDetalle] = useState([]);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [updatingRanges, setUpdatingRanges] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [canDesvalidar, setCanDesvalidar] = useState(false);
  const [desvalidando, setDesvalidando] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [showWAModal, setShowWAModal] = useState(false);
  const [waPhone, setWaPhone] = useState("");
  const [waPhoneOriginal, setWaPhoneOriginal] = useState("");
  const [waPropietarioNit, setWaPropietarioNit] = useState(null);
  const [waEditing, setWaEditing] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailAddressOriginal, setEmailAddressOriginal] = useState("");
  const [emailPropietarioNit, setEmailPropietarioNit] = useState(null);
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [manualOverrides, setManualOverrides] = useState(new Set());
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState("NA");
  const [loadingEquipo, setLoadingEquipo] = useState(false);
  const [empresa, setEmpresa] = useState("");
  const [imagenesExamen, setImagenesExamen] = useState({
    imagen1: null,
    imagen2: null,
    imagen3: null,
  });
  const API_URL = process.env.REACT_APP_API_URL;

  const query = new URLSearchParams(location.search);
  const paciente_cod = query.get("paciente_cod");
  const examen_cod = query.get("examen_cod");

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/util/param_sist`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.empresa) setEmpresa(d.empresa);
      })
      .catch(() => {});
  }, [API_URL]);

  useEffect(() => {
    const fetchDetalle = async () => {
      try {
        setIsInitialLoading(true);

        const { data } = await axios.get(`${API_URL}/results/result-detail`, {
          params: { paciente_cod, examen_cod },
        });

        const rows = Array.isArray(data) ? data : [];
        setDetalle(rows);
        setManualOverrides(computeManualOverrides(rows));
      } catch (err) {
        console.error("Error obteniendo detalle:", err);
        toast.error("No se pudo cargar la información del examen");
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchDetalle();
  }, [paciente_cod, examen_cod, API_URL]);

  useEffect(() => {
    if (!detalle.length) return;
    const usuario = user?.usuario || user?.id;
    if (!usuario) return;
    axios
      .get(`${API_URL}/results/can-desvalidar`, {
        params: { usuario, examen_cod },
      })
      .then(({ data }) => setCanDesvalidar(Boolean(data?.puede)))
      .catch(() => setCanDesvalidar(false));
    // eslint-disable-next-line
  }, [detalle.length, examen_cod, API_URL]);

  useEffect(() => {
    if (!detalle.length) return;
    const especie_cod = detalle[0]?.especie_cod;
    const equipoActual = detalle[0]?.equipo || "NA";
    if (!especie_cod) return;

    setEquipoSeleccionado(equipoActual);

    axios
      .get(`${API_URL}/results/equipos-referencia`, {
        params: { examen_cod, especie_cod },
      })
      .then(({ data }) => {
        const lista = data.map((e) => e.equipo_cod);
        if (lista.length > 0 && !lista.includes("NA")) lista.unshift("NA");
        setEquiposDisponibles(lista);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [detalle.length, examen_cod, API_URL]);

  const cambiarEquipo = async (nuevoEquipo) => {
    const especie_cod = detalle[0]?.especie_cod;
    if (!especie_cod) return;

    setEquipoSeleccionado(nuevoEquipo);
    setLoadingEquipo(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/results/referencias-equipo`,
        {
          params: { examen_cod, especie_cod, equipo_cod: nuevoEquipo, raza_cod: detalle[0]?.raza_cod },
        },
      );

      const refMap = new Map(data.map((r) => [String(r.analito_cod), r]));

      setDetalle((prev) =>
        prev.map((item) => {
          const ref = refMap.get(String(item.analito_cod));
          return {
            ...item,
            minimo: ref?.minimo ?? item.minimo,
            intermedio: ref?.intermedio ?? item.intermedio,
            maximo: ref?.maximo ?? item.maximo,
            equipo: nuevoEquipo,
          };
        }),
      );
    } catch {
      toast.error(
        "No se pudieron cargar las referencias para el equipo seleccionado",
      );
    } finally {
      setLoadingEquipo(false);
    }
  };

  useEffect(() => {
    const fetchImagenes = async () => {
      if (!detalle.length) return;

      try {
        setLoadingImages(true);
        const { data } = await axios.get(`${API_URL}/results/result-images`, {
          params: { paciente_cod, examen_cod },
        });

        setImagenesExamen({
          imagen1: data?.imagen1 || null,
          imagen2: data?.imagen2 || null,
          imagen3: data?.imagen3 || null,
        });
      } catch (err) {
        console.error("Error obteniendo imágenes del examen:", err);
        setImagenesExamen({ imagen1: null, imagen2: null, imagen3: null });
      } finally {
        setLoadingImages(false);
      }
    };

    fetchImagenes();
  }, [detalle.length, paciente_cod, examen_cod, API_URL]);

  const toggleManualOverride = (index) => {
    if (isValidated) return;
    setManualOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const updateResultado = (index, value) => {
    if (detalle[0]?.validado) return;
    if (detalle[index]?.formula) return;

    setDetalle((prev) => {
      const nextRows = prev.map((item, i) =>
        i === index ? { ...item, resultado: value } : item,
      );
      return applyFormulaCalculations(nextRows);
    });
  };

  const guardarResultados = async () => {
    if (detalle[0]?.validado) {
      toast.warning("El examen está validado y no permite cambios");
      return;
    }

    if (!detalle.length) {
      toast.error("No hay analitos para registrar");
      return;
    }

    const respondido_por = user?.usuario || user?.id;

    if (!respondido_por) {
      toast.error("No se pudo identificar el usuario que responde");
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_URL}/results/respond`, {
        paciente_cod,
        examen_cod,
        respondido_por,
        resultados: detalle.map((item) => ({
          analito_cod: item.analito_cod,
          analito: item.analito,
          resultado: item.resultado || "",
          unidades: item.unidades || "",
          minimo: item.minimo || "",
          intermedio: item.intermedio || "",
          maximo: item.maximo || "",
          equipo: item.equipo || "NA",
          reactivo: item.reactivo || "NA",
        })),
      });

      toast.success("Resultados registrados correctamente");

      const res = await axios.get(`${API_URL}/results/result-detail`, {
        params: { paciente_cod, examen_cod },
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setDetalle(rows);
      setManualOverrides(computeManualOverrides(rows));
    } catch (err) {
      console.error("Error guardando resultados:", err);
      toast.error("No se pudieron registrar los resultados");
    } finally {
      setSaving(false);
    }
  };

  const validarExamen = async () => {
    if (!detalle.length) {
      toast.error("No hay analitos para validar");
      return;
    }

    if (detalle[0]?.validado) {
      toast.info("El examen ya se encuentra validado");
      return;
    }

    const validado_por = user?.usuario || user?.id;

    if (!validado_por) {
      toast.error("No se pudo identificar el usuario que valida");
      return;
    }

    const confirmado = window.confirm(
      "¿Está seguro de validar este examen? Luego no podrá modificar resultados ni respuestas.",
    );

    if (!confirmado) return;

    try {
      setValidating(true);
      await axios.post(`${API_URL}/results/validate`, {
        paciente_cod,
        examen_cod,
        validado_por,
      });

      toast.success("Examen validado correctamente");

      const res = await axios.get(`${API_URL}/results/result-detail`, {
        params: { paciente_cod, examen_cod },
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setDetalle(rows);
      setManualOverrides(computeManualOverrides(rows));
    } catch (err) {
      console.error("Error validando examen:", err);
      const message =
        err?.response?.data?.error || "No se pudo validar el examen";
      toast.error(message);
    } finally {
      setValidating(false);
    }
  };

  const desvalidarExamen = async () => {
    const usuario = user?.usuario || user?.id;
    if (!usuario) {
      toast.error("No se pudo identificar el usuario");
      return;
    }

    const confirmado = window.confirm(
      "¿Está seguro de desvalidar este examen? Se borrarán los datos de validación y quedará pendiente de validar nuevamente.",
    );
    if (!confirmado) return;

    try {
      setDesvalidando(true);
      await axios.post(`${API_URL}/results/desvalidar`, {
        paciente_cod,
        examen_cod,
        usuario,
      });
      toast.success("Examen desvalidado correctamente");
      const res = await axios.get(`${API_URL}/results/result-detail`, {
        params: { paciente_cod, examen_cod },
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setDetalle(rows);
      setManualOverrides(computeManualOverrides(rows));
    } catch (err) {
      console.error("Error desvalidando examen:", err);
      toast.error(err?.response?.data?.error || "No se pudo desvalidar el examen");
    } finally {
      setDesvalidando(false);
    }
  };

  const eliminarResultado = async () => {
    if (isValidated) return;

    const nit = detalle[0]?.documento;
    if (!nit) {
      toast.error("No se pudo identificar el NIT del propietario");
      return;
    }

    const confirmado = window.confirm(
      "¿Está seguro de eliminar el resultado? Se borrarán todos los datos ingresados y el examen quedará pendiente.",
    );
    if (!confirmado) return;

    try {
      setDeleting(true);
      await axios.delete(`${API_URL}/results/delete-result`, {
        data: { paciente_cod, examen_cod, nit },
      });
      toast.success("Resultado eliminado correctamente");
      navigate(0);
    } catch (err) {
      console.error("Error eliminando resultado:", err);
      toast.error(
        err?.response?.data?.error || "No se pudo eliminar el resultado",
      );
    } finally {
      setDeleting(false);
    }
  };

  const actualizarValoresReferencia = async () => {
    try {
      setUpdatingRanges(true);

      const { data } = await axios.post(
        `${API_URL}/results/update-reference-ranges`,
        {
          paciente_cod,
          examen_cod,
        },
      );

      const res = await axios.get(`${API_URL}/results/result-detail`, {
        params: { paciente_cod, examen_cod },
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setDetalle(rows);
      setManualOverrides(computeManualOverrides(rows));

      toast.success(
        `Referencias actualizadas: ${data.actualizados}/${data.total_analitos}. Sin referencia: ${data.sin_referencia}`,
      );
    } catch (err) {
      console.error("Error actualizando valores de referencia:", err);
      toast.error(
        err?.response?.data?.error ||
          "No se pudieron actualizar los valores de referencia",
      );
    } finally {
      setUpdatingRanges(false);
    }
  };

  // 📱 Abrir modal WhatsApp
  const openWAModal = async () => {
    if (!examen) return;
    const nit = examen.documento;
    setWaPropietarioNit(nit || null);
    setWaEditing(false);
    if (nit) {
      try {
        const { data } = await axios.get(
          `${API_URL}/whatsapp/telefono-propietario/${nit}`,
        );
        setWaPhone(data.telefono || "");
        setWaPhoneOriginal(data.telefono || "");
      } catch (_) {
        setWaPhone("");
        setWaPhoneOriginal("");
      }
    }
    setShowWAModal(true);
  };

  const handleSendWA = async () => {
    if (!waPhone.trim()) {
      toast.warning("Ingresa un número de teléfono");
      return;
    }
    setWaSending(true);
    try {
      // Si el número fue editado, actualizarlo en BD
      if (waEditing && waPhone !== waPhoneOriginal && waPropietarioNit) {
        await axios.patch(
          `${API_URL}/whatsapp/propietario/${waPropietarioNit}/telefono`,
          { telefono: waPhone },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setWaPhoneOriginal(waPhone);
        setWaEditing(false);
      }

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/whatsapp/enviar-resultado`,
        {
          paciente_cod,
          examen_cod,
          empresa,
          telefono: waPhone,
          enviado_por: user?.usuario || user?.id || null,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );
      toast.success("Resultado enviado por WhatsApp correctamente");
      setShowWAModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error enviando por WhatsApp");
    } finally {
      setWaSending(false);
    }
  };

  // ✉️ Abrir modal de correo
  const openEmailModal = async () => {
    if (!examen) return;
    const nit = examen.documento;
    setEmailPropietarioNit(nit || null);
    setEmailEditing(false);
    if (nit) {
      try {
        const { data } = await axios.get(
          `${API_URL}/email/correo-propietario/${nit}`,
        );
        setEmailAddress(data.email || "");
        setEmailAddressOriginal(data.email || "");
      } catch (_) {
        setEmailAddress("");
        setEmailAddressOriginal("");
      }
    }
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailAddress.trim()) {
      toast.warning("Ingresa un correo electrónico");
      return;
    }
    setEmailSending(true);
    try {
      if (emailEditing && emailAddress !== emailAddressOriginal && emailPropietarioNit) {
        await axios.patch(
          `${API_URL}/email/propietario/${emailPropietarioNit}/correo`,
          { email: emailAddress },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
        );
        setEmailAddressOriginal(emailAddress);
        setEmailEditing(false);
      }

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/email/enviar-resultado`,
        {
          paciente_cod,
          examen_cod,
          correo: emailAddress,
          enviado_por: user?.usuario || user?.id || null,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
      );
      toast.success("Resultado enviado por correo correctamente");
      setShowEmailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error enviando por correo");
    } finally {
      setEmailSending(false);
    }
  };

  // 📄 Descargar reporte PDF
  const descargarPDF = async () => {
    try {
      setLoadingPdf(true);
      const res = await axios.get(`${API_URL}/results/reporte`, {
        params: { paciente_cod, examen_cod },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resultado_${paciente_cod}_${examen_cod}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("Error descargando PDF:", err);
      alert("❌ Error al generar el reporte");
    } finally {
      setLoadingPdf(false);
    }
  };

  if (isInitialLoading) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center h-[70vh] gap-4">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-brand-100">
            <PawPrint className="w-10 h-10 text-brand-600 animate-bounce" />
          </div>

          <h2 className="text-lg font-semibold text-gray-700">
            Procesando resultados del paciente
          </h2>

          <div className="w-40 h-1 bg-brand-100 rounded overflow-hidden">
            <div className="h-full bg-brand-600 animate-pulse"></div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!detalle || detalle.length === 0) {
    return (
      <AppLayout>
        <div className="p-6 text-center">
          <PawPrint className="w-10 h-10 text-brand-300 mx-auto mb-3" />

          <h1 className="text-xl font-semibold mb-2">
            No hay resultados disponibles
          </h1>

          <p className="text-gray-600 mb-6">
            Este examen aún no tiene analitos registrados.
          </p>

          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </button>
        </div>
      </AppLayout>
    );
  }

  const examen = detalle[0];
  const isValidated = Boolean(examen?.validado);
  const isRespondido = Boolean(examen?.contestado);

  const getImageSrc = (img) => {
    if (!img) return "";

    if (img.startsWith("data:image")) {
      return img;
    }

    if (img.startsWith("/9j/")) {
      return `data:image/jpeg;base64,${img}`;
    }

    if (img.startsWith("iVBOR")) {
      return `data:image/png;base64,${img}`;
    }

    if (img.includes("JFIF") || img.includes("ÿØ")) {
      const base64 = btoa(
        new Uint8Array([...img].map((c) => c.charCodeAt(0))).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          "",
        ),
      );

      return `data:image/jpeg;base64,${base64}`;
    }

    return "";
  };

  return (
    <AppLayout>
      <div className="p-6">
        <div className="flex gap-3 mb-4 flex-wrap">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver atrás
          </button>

          <button
            onClick={guardarResultados}
            disabled={saving || isValidated}
            className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar resultados"}
          </button>

          <button
            onClick={validarExamen}
            disabled={validating || isValidated || !isRespondido}
            className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {isValidated
              ? "Examen validado"
              : validating
                ? "Validando..."
                : "Validar examen"}
          </button>

          {canDesvalidar && isValidated && (
            <button
              onClick={desvalidarExamen}
              disabled={desvalidando}
              className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              {desvalidando ? "Desvalidando..." : "Desvalidar"}
            </button>
          )}

          <button
            onClick={descargarPDF}
            disabled={loadingPdf || !isValidated}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {loadingPdf ? "Generando..." : "Descargar PDF"}
          </button>

          <button
            onClick={openWAModal}
            disabled={!isValidated}
            className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Enviar por WhatsApp
          </button>

          <button
            onClick={openEmailModal}
            disabled={!isValidated}
            className="inline-flex items-center px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Mail className="w-4 h-4 mr-2" />
            Enviar por Correo
          </button>
        </div>

        <h1 className="text-2xl font-semibold mb-6 text-gray-800">
          Detalle del resultado
        </h1>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-blue-700 mb-1">
            {examen.examen_nombre}
          </h2>
          <p className="text-sm text-gray-600">
            <strong>Código paciente:</strong> {examen.paciente_cod} <br />
            <strong>Propietario:</strong> {examen.propietario} <br />
            <strong>Paciente:</strong> {examen.paciente} <br />
            <strong>Edad:</strong> {examen.edad} <br />
            <strong>Sexo:</strong> {examen.genero} <br />
            <strong>Especie:</strong> {examen.especie} <br />
            <strong>Raza:</strong> {examen.raza} <br />
            <strong>Código examen:</strong> {examen.examen_cod} <br />
            <strong>Fecha:</strong>{" "}
            {new Date(examen.fecha).toLocaleDateString("es-CO")} <br />
            <strong>Estado:</strong>{" "}
            {isValidated ? "Validado" : "Pendiente de validación"} <br />
            <strong>Validado por:</strong> {examen.validado_por || "-"}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <div className="flex items-center justify-between p-4 border-b bg-gray-50 flex-wrap gap-3">
            {equiposDisponibles.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Valores de referencia por equipo:
                </label>
                <select
                  value={equipoSeleccionado}
                  onChange={(e) => cambiarEquipo(e.target.value)}
                  disabled={loadingEquipo || isValidated}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {equiposDisponibles.map((eq) => (
                    <option key={eq} value={eq}>
                      {eq}
                    </option>
                  ))}
                </select>
                {loadingEquipo && (
                  <span className="text-xs text-gray-500">Cargando...</span>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={actualizarValoresReferencia}
                disabled={updatingRanges || isValidated}
                className="inline-flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <RefreshCcw className="w-4 h-4 mr-2" />
                {updatingRanges
                  ? "Actualizando referencias..."
                  : "Actualizar valores de referencia"}
              </button>
              <button
                onClick={eliminarResultado}
                disabled={deleting || isValidated}
                className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
                title={
                  isValidated
                    ? "No se puede eliminar un examen validado"
                    : "Eliminar resultado"
                }
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleting ? "Eliminando..." : "Eliminar resultado"}
              </button>
            </div>
          </div>
          <table className="w-full text-sm border-collapse">
            <thead className="bg-blue-100 text-gray-800 border-b">
              <tr>
                <th className="p-3 text-left">Código</th>
                <th className="p-3 text-left">Analito</th>
                <th className="p-3 text-left">Resultado</th>
                <th className="p-3 text-left">Unidades</th>
                <th className="p-3 text-left">Mínimo</th>
                <th className="p-3 text-left">Intermedio</th>
                <th className="p-3 text-left">Máximo</th>
              </tr>
            </thead>
            <tbody>
              {detalle.map((item, index) => (
                <tr
                  key={index}
                  className="border-b hover:bg-gray-50 transition duration-100"
                >
                  <td className="p-3 font-medium text-gray-700">
                    {item.analito_cod || "-"}
                  </td>
                  <td className="p-3 font-medium text-gray-700">
                    {item.analito || "-"}
                  </td>
                  <td className="p-3 text-gray-800">
                    {item.comodin_cod &&
                    Array.isArray(item.comodin_opciones) &&
                    item.comodin_opciones.length > 0 ? (
                      <div className="flex items-center gap-2">
                        {manualOverrides.has(index) ? (
                          <input
                            type="text"
                            value={item.resultado || ""}
                            onChange={(e) =>
                              updateResultado(index, e.target.value)
                            }
                            disabled={isValidated}
                            className="w-full min-w-32 border rounded px-2 py-1 disabled:bg-gray-100 disabled:text-gray-500"
                            placeholder="Ingrese resultado"
                          />
                        ) : (
                          <SelectField
                            name={`resultado_${index}`}
                            value={item.resultado || ""}
                            onChange={(e) =>
                              updateResultado(index, e.target.value)
                            }
                            disabled={isValidated || Boolean(item.formula)}
                            placeholder="Seleccione una opción"
                            options={item.comodin_opciones.map((op) => ({
                              valor: op.detalle || op.codigo,
                              etiqueta: op.detalle || op.codigo,
                            }))}
                            valueKey="valor"
                            displayFn={(op) => op.etiqueta}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => toggleManualOverride(index)}
                          disabled={isValidated || Boolean(item.formula)}
                          title={
                            manualOverrides.has(index)
                              ? "Usar selector"
                              : "Digitar manualmente"
                          }
                          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                            manualOverrides.has(index)
                              ? "bg-blue-600"
                              : "bg-gray-300"
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              manualOverrides.has(index)
                                ? "translate-x-4"
                                : "translate-x-0"
                            }`}
                          />
                        </button>
                      </div>
                    ) : (
                      <input
                        type={item.tipo_resp === "N" ? "number" : "text"}
                        step={item.tipo_resp === "N" ? "any" : undefined}
                        value={item.resultado || ""}
                        onChange={(e) => updateResultado(index, e.target.value)}
                        disabled={isValidated || Boolean(item.formula)}
                        className="w-full min-w-32 border rounded px-2 py-1 disabled:bg-gray-100 disabled:text-gray-500"
                        placeholder={
                          item.tipo_resp === "N"
                            ? "Ingrese resultado numérico"
                            : "Ingrese resultado"
                        }
                      />
                    )}
                  </td>
                  <td className="p-3 text-gray-600">{item.unidades || "-"}</td>
                  <td className="p-3 text-gray-600">{item.minimo || "-"}</td>
                  <td className="p-3 text-gray-600">
                    {item.intermedio || "-"}
                  </td>
                  <td className="p-3 text-gray-600">{item.maximo || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loadingImages && (
          <div className="bg-white rounded-lg shadow p-6 mt-6 text-sm text-gray-500">
            Cargando imágenes del examen...
          </div>
        )}

        {[
          imagenesExamen.imagen1,
          imagenesExamen.imagen2,
          imagenesExamen.imagen3,
        ].some(Boolean) && (
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h3 className="text-lg font-semibold text-blue-700 mb-4">
              Imágenes del examen
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                imagenesExamen.imagen1,
                imagenesExamen.imagen2,
                imagenesExamen.imagen3,
              ]
                .filter(Boolean)
                .map((img, idx) => (
                  <img
                    key={idx}
                    src={getImageSrc(img)}
                    alt={`Imagen ${idx + 1} del examen`}
                    className="w-full h-auto rounded border border-gray-200"
                  />
                ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Modal envío por WhatsApp ─────────────────────────── */}
      {showWAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <MessageCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-800">
                  Enviar resultado por WhatsApp
                </h3>
              </div>
              <button
                onClick={() => setShowWAModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 space-y-1">
                <p>
                  <span className="font-medium">Paciente:</span>{" "}
                  {examen?.paciente}
                </p>
                <p>
                  <span className="font-medium">Examen:</span>{" "}
                  {examen?.examen_nombre}
                </p>
                <p>
                  <span className="font-medium">Código:</span> {paciente_cod} /{" "}
                  {examen_cod}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Número de WhatsApp del propietario
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={waPhone}
                    onChange={(e) => setWaPhone(e.target.value)}
                    disabled={!waEditing}
                    placeholder="Ej: 3001234567"
                    className="flex-1 border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <button
                    onClick={() => setWaEditing((v) => !v)}
                    className={`p-2 rounded-xl border transition-all ${waEditing ? "bg-brand-100 border-brand-300 text-brand-700" : "border-gray-200 text-gray-500 hover:border-brand-200"}`}
                    title={waEditing ? "Cancelar edición" : "Editar número"}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                {waEditing && waPhone !== waPhoneOriginal && (
                  <p className="text-xs text-amber-600 mt-1">
                    El número actualizado se guardará en la base de datos al
                    enviar.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end p-5 border-t border-gray-100">
              <button
                onClick={() => setShowWAModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendWA}
                disabled={waSending || !waPhone.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {waSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {waSending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal envío por Correo ───────────────────────────── */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-sky-100 rounded-xl">
                  <Mail className="h-5 w-5 text-sky-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-800">
                  Enviar resultado por correo
                </h3>
              </div>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 space-y-1">
                <p>
                  <span className="font-medium">Paciente:</span> {examen?.paciente}
                </p>
                <p>
                  <span className="font-medium">Examen:</span> {examen?.examen_nombre}
                </p>
                <p>
                  <span className="font-medium">Código:</span> {paciente_cod} / {examen_cod}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Correo del propietario
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    disabled={!emailEditing}
                    placeholder="ejemplo@correo.com"
                    className="flex-1 border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <button
                    onClick={() => setEmailEditing((v) => !v)}
                    className={`p-2 rounded-xl border transition-all ${emailEditing ? "bg-brand-100 border-brand-300 text-brand-700" : "border-gray-200 text-gray-500 hover:border-brand-200"}`}
                    title={emailEditing ? "Cancelar edición" : "Editar correo"}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                {emailEditing && emailAddress !== emailAddressOriginal && (
                  <p className="text-xs text-amber-600 mt-1">
                    El correo actualizado se guardará en la base de datos al enviar.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end p-5 border-t border-gray-100">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailSending || !emailAddress.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {emailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {emailSending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
